/* eslint-disable max-len */
import { differenceInDays } from 'date-fns';
import ifr from 'iframe-resizer';
import { shade } from 'polished';
import React, { useEffect, useMemo, useState } from 'react';
import Scrollbars from 'react-custom-scrollbars';
import { useEffectOnce } from 'react-use';
import styled from 'styled-components';
import { Images } from '../../@resources/img';
import { telemetry } from '../../@vendor/Posthog';
import { CloseButton, waitFor } from '../../CodesThatsGottaGoSomewhereElse';
import { Host } from '../../Host';
import { LFC } from '../../Legacy';
import { LabelValue } from '../../platform/types/LabelValue';
import { ButtonToggle } from '../../ui/ButtonToggle';
import { Check } from '../../ui/Check';
import { DropButton } from '../../ui/DropButton';
import { Position } from '../../ui/Position';
import { Shadows } from '../../ui/Standards';
import { useResolve } from '../Container';
import { ProfileModel } from '../ProfileModel';
import { AccountModel } from '../account/AccountModel';
import { Copy, Flexer, LinkButton, Title } from '../dashboard/elements/LayoutElements';
import { Popup, PopupProps } from '../popups/Popup';
import { PopupManager } from '../popups/PopupManager';
import { Colors, Prompt } from './../../ui';

const XScrollbars = Scrollbars as any;
const { iframeResize } = ifr;

const PlanInfo = (color: string, name: string, title: string, desc: string, pricing: string[], pricingNote: string[], features: string[][], action: 'none' | 'buy' | 'contact') =>
    ({ color, name, title, desc, pricing, pricingNote, features, action });
type PlanInfo = ReturnType<typeof PlanInfo>;

const plans = [
    PlanInfo(
        Colors.primaryLight,
        'free', 'Free',
        'For small teams or those just getting started, try a limited version of Metro Retro for free.',
        ['$0 per member', '$0 per member'],
        ['with usage limits', 'with usage limits'],
        [[
            'Usage limits apply',
            'Single shared workspace',
            'Templates for retros, ideation, planning & workshops',
            'Basic facilitator controls',
        ]],
        'none',
    ),
    PlanInfo(
        Colors.positive,
        'pro', 'Pro',
        'For individuals or small teams who want the best collaboration experience.',
        ['$12 per member', '$120 per member'],
        ['per month', 'per year'],
        [[
            'Unlimited Boards',
            '10 Guests per board (no-signup)',
            'Workspaces & Folders',
            'Advanced facillitator controls (Hide Identities, Ready Check, Polls, etc)',
            'Email & Chat Support',
        ], [
        ]],
        'buy',
    ),
    PlanInfo(
        Colors.mystery,
        'business', 'Business',
        'For for medium to large organizations requiring SSO & improved security.',
        ['Contact Us', 'Contact Us'],
        ['for custom pricing options', 'for custom pricing options'],
        [[
            'Everything from Pro, plus...',
            'Unlimited Guests (no-signup)',
            'Single Sign-On (SAML)',
            'Personalized Onboarding',
            'Custom Contracts',
            '99.9% Uptime SLA',
            'On-Premise Hosting (optional)',
        ], [
        ]],
        'contact',
    ),
];

const UpgradeOption = styled.div<{ color: string }>`
    display: flex;
    width: 22rem;
    flex-direction: column;
    padding: 2rem 1rem;
    background: white;
    border-radius: .25rem;
    border-bottom: solid .25rem ${p => shade(.1, p.color)};
    /* color: white; */
    box-shadow: ${Shadows.md};

    ${Title} {
        text-align: center;
    }

    ${Copy} {
        color: ${Colors.text};
    }

`;

const UpgradeTier = styled.div`
    display: flex;
    flex: 0 0 32%;
`;

const UpgradeCost = styled.div`
    font-size: 1.75rem;
    text-align: center;
    color: ${Colors.text};
`;

const UpgradeCostNote = styled.div`
    margin-top: -.125rem;
    font-size: 1rem;
    opacity: .8;
    text-align: center;
    color: ${Colors.textLight};
`;

const UpgradeFeature = styled.div`
    position: relative;
    padding: 0 0 0.5rem 2rem;
    font-size: .875rem;
    line-height: 1.55rem;
    color: ${Colors.text};

    &:after {
        content: '';
        position: absolute;
        left: 0rem;
        top: 0rem;
        height: 1.5rem;
        width: 1.5rem;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cg transform='translate(3, 4) scale(.75)'%3E%3Cpolyline fill='none' stroke='%23546077' stroke-width='3' stroke-linecap='round' stroke-linejoin='round' points='20 6 9 17 4 12'%3E%3C/polyline%3E%3C/g%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center center;
    }
`;

const UpgradeDesc = styled.div`
    /* padding: 0 .75rem;
    margin: 1.5rem 0;
    font-size: .875rem;
    color: white; */

    padding: 1.25rem .75rem 0;
    margin: 1.25rem 0;
    font-size: .875rem;
    color: ${Colors.text};
    border-top: solid 1px rgb(255 255 255 / 66%);
`;

const UpgradeStatus = styled.div`
    padding: .5rem .75rem;
    font-size: .875rem;
    background: rgba(0,0,0,0.05);
`;

const UpgradeAnnualGraphic = () =>
    <svg width="180" height="95" viewBox="0 0 180 95" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M49.9633 34.5487C50.7257 34.4186 51.1562 34.6423 51.2547 35.2199C51.3533 35.7975 51.1721 36.2683 50.7111 36.6323C50.2462 36.9731 49.7001 37.2565 49.0729 37.4824C48.4688 37.7044 47.8915 37.8742 47.341 37.9919C46.7865 38.0865 46.4323 38.1707 46.2785 38.2445C46.1438 38.2913 45.8246 38.4408 45.3208 38.6932C44.8361 38.9186 44.2535 39.1963 43.5727 39.5264C42.888 39.8335 42.1495 40.1734 41.3572 40.5464C38.8648 41.6849 36.8446 42.3863 35.2966 42.6504C34.5111 42.7844 33.96 42.7595 33.6434 42.5758C33.3459 42.365 33.2405 42.0263 33.3274 41.5598C34.8 39.7393 36.267 37.468 37.7284 34.746C39.913 30.6879 40.8338 27.6539 40.4908 25.6439C40.4238 25.2511 40.303 24.8913 40.1283 24.5645C39.752 23.8916 39.0147 23.5419 37.9165 23.5153C36.8999 23.6887 36.1036 24.0386 35.5277 24.5648C34.9518 25.091 34.4183 25.6576 33.9275 26.2644C33.4597 26.8673 32.961 27.4279 32.4312 27.9463C31.9246 28.4607 31.2438 28.7908 30.389 28.9367C30.282 28.1703 30.4678 27.3778 30.9463 26.5591C31.8954 24.8755 33.4122 23.523 35.4967 22.5016C36.2388 22.1134 36.8409 21.8799 37.303 21.8011C37.7651 21.7222 38.1136 21.6747 38.3486 21.6583C39.2654 21.597 40.0945 21.7884 40.8361 22.2325C41.5776 22.6767 42.1046 23.4664 42.4172 24.6019C42.973 27.8595 42.0651 31.7354 39.6934 36.2296C39.047 37.4573 38.3983 38.6023 37.7473 39.6644C38.0054 39.6441 38.4001 39.5887 38.9315 39.498C39.486 39.4034 40.2152 39.1482 41.1191 38.7325C42.019 38.2936 42.9688 37.7987 43.9685 37.2477C44.9643 36.6736 45.9524 36.1246 46.933 35.6007C47.9135 35.0767 48.9236 34.7261 49.9633 34.5487Z" fill="#3B65EF" />
        <path d="M70.5404 36.4232C69.6602 36.9775 69.0583 37.2823 68.7349 37.3375C68.4345 37.3888 68.2207 37.3896 68.0933 37.34C67.966 37.2904 67.8113 37.1503 67.6294 36.9198C67.4993 36.1574 67.3588 35.1947 67.2079 34.0316C67.0801 32.8646 66.927 31.6187 66.7485 30.2939C66.57 28.9691 66.3665 27.6367 66.1378 26.2967C65.9092 24.9567 65.631 23.744 65.3031 22.6587C65.177 21.9194 65.6107 21.465 66.6041 21.2955C66.8352 21.2561 67.0794 21.6424 67.3368 22.4544C67.6173 23.2625 67.8201 23.8936 67.9452 24.3478C68.0933 24.798 68.2082 25.1232 68.2899 25.3232C68.7053 24.1349 69.7493 23.077 71.4219 22.1497C72.3179 21.6877 73.1818 21.3858 74.0135 21.2439C74.8452 21.102 75.5907 21.0818 76.25 21.1833C76.9284 21.2577 77.5325 21.5232 78.0621 21.9797C78.6109 22.4091 78.9582 23.0513 79.1041 23.9061C79.5116 22.6716 80.6897 21.4244 82.6384 20.1646C84.4102 18.9826 86.0585 18.2615 87.5833 18.0014C89.7782 17.6269 90.9944 18.2753 91.232 19.9467L94.1593 32.7143C94.179 32.8298 93.8578 32.9679 93.1957 33.1284C91.9177 33.4415 91.2511 33.4364 91.1959 33.113C91.0939 32.6548 91.0035 32.1947 90.9247 31.7326L90.146 26.1236C89.7006 23.5128 89.0936 21.4884 88.3252 20.0503C86.8201 20.426 85.6851 20.8812 84.9204 21.4158C84.1749 21.9235 83.5395 22.5193 83.0142 23.2033C82.485 23.8642 82.0586 24.7097 81.7351 25.7397C81.0119 28.0505 80.9498 30.9618 81.549 34.4736C81.4932 34.9824 81.084 35.3018 80.3216 35.4319C79.8133 35.5187 79.3837 35.5087 79.0326 35.4022C78.6777 35.2726 78.523 35.1325 78.5686 34.9821C78.5898 32.4582 78.4841 30.5147 78.2515 29.1516C78.0662 28.0657 77.7753 27.0573 77.3787 26.1264C77.0013 25.1684 76.5424 24.0816 76.0022 22.8661C72.6752 23.4338 70.6608 25.1446 69.959 27.9986C69.6517 29.2636 69.5521 30.4219 69.6602 31.4733C69.7875 32.4978 69.9141 33.3797 70.0403 34.119C70.1625 34.8352 70.3292 35.6033 70.5404 36.4232Z" fill="#3B65EF" />
        <path d="M95.122 26.0235C94.6568 23.2973 95.4606 20.9014 97.5334 18.8359C99.1801 17.1997 101.297 16.0183 103.883 15.2916C104.022 15.268 104.345 15.2128 104.854 15.1261C105.362 15.0394 106.069 15.0733 106.975 15.2278C108.98 15.5514 110.469 16.4743 111.442 17.9964C111.829 18.596 112.063 19.1268 112.142 19.5889C112.216 20.0279 112.258 20.3418 112.267 20.5305C112.759 23.4185 112.05 25.8814 110.139 27.9194C108.366 29.7911 105.955 30.9871 102.905 31.5074C100.71 31.8819 98.9601 31.6575 97.6546 30.8341C96.3722 30.0067 95.528 28.4032 95.122 26.0235ZM97.6551 26.4473C97.8995 27.8797 98.4463 28.785 99.2954 29.1632C99.9361 29.4343 100.938 29.4536 102.301 29.221C103.664 28.9884 104.848 28.6795 105.852 28.2942C106.856 27.9089 107.719 27.3931 108.441 26.7468C109.985 25.3422 110.557 23.4732 110.159 21.1397C109.923 19.7535 109.36 18.6845 108.471 17.9327C107.501 17.1234 106.357 16.8311 105.04 17.0558C104.167 17.3712 103.284 17.7002 102.391 18.0427C101.522 18.3813 100.728 18.8853 100.01 19.5546C98.4471 20.9863 97.6622 23.2838 97.6551 26.4473Z" fill="#3B65EF" />
        <path d="M127.561 19.4545C127.092 16.7052 126.597 14.7093 126.076 13.4667C126.202 13.0886 125.895 12.9626 125.156 13.0888C124.37 13.2228 123.387 13.5926 122.206 14.1982C121.026 14.8039 120.076 15.3701 119.358 15.8969C117.64 17.1172 116.75 18.1725 116.688 19.0628L118.296 28.4891L115.975 28.8853C115.54 28.151 115.014 25.9702 114.395 22.3429C113.843 19.1084 113.495 16.7189 113.35 15.1744C113.228 13.6259 113.184 12.7417 113.218 12.522C113.752 12.0267 114.204 11.7476 114.573 11.6845C115.151 11.586 115.556 12.2182 115.789 13.5814C115.879 14.1127 115.933 14.6385 115.951 15.1586C115.987 15.6517 116.021 15.9906 116.053 16.1754C119.327 12.7875 121.992 10.9181 124.048 10.5673C126.381 10.1692 128.031 11.8255 128.997 15.5362C129.36 16.8294 129.741 18.6428 130.139 20.9762L130.572 23.7199C130.745 24.7365 130.936 25.7145 131.143 26.6538C130.936 26.8319 130.763 26.9327 130.624 26.9564C130.551 26.9451 130.395 26.9361 130.156 26.9293C129.917 26.9225 129.611 26.9391 129.237 26.979C128.849 26.0943 128.562 25.1803 128.377 24.237C128.189 23.2706 128.025 22.3831 127.887 21.5744L127.561 19.4545Z" fill="#3B65EF" />
        <path d="M137.749 22.103L136.873 16.5463C136.723 15.6683 136.514 14.9312 136.246 14.335L130.874 15.2515L130.397 12.8721L135.595 11.9852L134.07 3.0441C134.104 2.82434 134.207 2.59275 134.379 2.34934C134.647 1.97071 134.886 1.76365 135.093 1.72817C135.324 1.68875 135.525 1.67835 135.694 1.69696C135.864 1.71558 136.065 1.84774 136.297 2.09345C136.927 3.55522 137.504 5.82249 138.028 8.89528C138.158 9.6577 138.321 10.4027 138.516 11.1301C139.24 10.9116 139.971 10.7392 140.711 10.6131C140.711 10.6131 141.311 10.5106 142.513 10.3056C142.952 10.2307 143.352 10.1386 143.714 10.0294L144.036 12.5423L138.952 13.267L141.166 25.1935C140.781 25.4493 140.439 25.6028 140.139 25.6541C138.983 25.8512 138.187 24.6675 137.749 22.103Z" fill="#3B65EF" />
        <path d="M163.637 20.6106C163.496 21.0388 162.986 21.3278 162.108 21.4776C161.254 21.6235 160.773 21.3845 160.667 20.7607C160.201 18.0345 159.824 16.1729 159.535 15.1761C158.945 12.9705 158.301 11.844 157.604 11.7966C155.885 11.757 154.494 11.8278 153.431 12.0092C152.392 12.1865 151.581 12.6578 150.998 13.423C150.434 14.1611 150.094 15.301 149.976 16.8427C149.878 18.3573 149.975 20.4569 150.267 23.1414L147.598 23.5967C147.42 22.2719 147.234 21.0435 147.041 19.9114C146.847 18.7794 146.596 17.4433 146.285 15.9032C145.971 14.3401 145.622 12.6402 145.237 10.8038C144.759 8.69795 144.396 6.91728 144.147 5.46175C143.895 3.98312 143.757 2.61579 143.733 1.35976C143.889 0.88137 144.186 0.604729 144.625 0.529833C145.157 0.439169 145.606 0.564596 145.973 0.906113C146.337 1.22453 146.556 1.53194 146.63 1.82834L147.432 7.57605C147.799 9.72469 148.177 11.3127 148.566 12.3399C150.409 10.599 152.439 9.53925 154.657 9.16083C155.374 9.03863 156.146 8.96631 156.974 8.94386C158.861 9.54926 160.156 10.3149 160.861 11.2408C161.206 11.7288 161.464 12.2673 161.636 12.8561C161.808 13.445 161.97 14.1187 162.124 14.8772C162.712 17.4873 163.216 19.3985 163.637 20.6106Z" fill="#3B65EF" />
        <path d="M175.328 9.91369C177.87 9.48008 179.251 9.91018 179.472 11.204C179.637 12.1743 179.126 13.4266 177.937 14.9609C176.162 17.2372 173.594 19.2565 170.233 21.0187C170.071 21.0463 169.859 20.9161 169.596 20.6282C169.356 20.3362 169.164 20.1193 169.021 19.9773C168.897 19.8082 168.808 19.7045 168.754 19.6662C171.261 18.3349 173.263 16.8996 174.76 15.3603C175.773 14.3315 176.234 13.5514 176.144 13.02C176.053 12.4887 175.326 12.3392 173.963 12.5718C172.6 12.8044 170.657 13.4688 168.133 14.5651C166.924 14.7239 165.811 14.4738 164.796 13.815C163.911 13.2289 163.407 12.5777 163.285 11.8615C163.159 11.1222 163.253 10.4167 163.566 9.74503C163.876 9.05028 164.332 8.37808 164.934 7.72844C165.536 7.0788 166.262 6.45566 167.111 5.85902C169.19 4.38684 171.443 3.4438 173.869 3.0299C174.585 2.90771 175.29 2.85876 175.983 2.88307C176.538 2.78846 176.903 2.97586 177.078 3.44527C177.253 3.91468 177.435 4.70387 177.625 5.81285C176.155 5.42162 174.565 5.37186 172.856 5.66356C170.73 6.02621 169.047 6.75321 167.807 7.84456C166.516 8.92069 165.962 10.1327 166.145 11.4806C166.255 12.1275 166.784 12.3701 167.731 12.2085L170.214 11.4996C172.307 10.667 174.012 10.1384 175.328 9.91369Z" fill="#3B65EF" />
        <path d="M102.354 61.4124C102.47 61.3927 102.581 61.6947 102.687 62.3185C102.794 62.9423 102.89 63.3658 102.975 63.589L91.3369 67.0726L93.2734 74.6597L90.4426 75C90.0879 72.9207 89.4508 70.5804 88.5315 67.9792L84.3219 70.4807C84.0216 70.5319 83.7379 70.402 83.4708 70.0909C82.9987 69.5533 82.7212 69.0419 82.6384 68.5567L87.5868 65.7866C86.4467 61.8916 85.6657 58.7081 85.244 56.236C84.8182 53.7408 84.6951 51.7646 84.8744 50.3074C85.0538 48.8502 85.4968 47.8236 86.2035 47.2275C86.9293 46.6044 87.7428 46.2159 88.6438 46.0622C89.5448 45.9085 90.4909 45.9491 91.4821 46.1842C92.4964 46.4154 93.4822 46.7584 94.4395 47.2132C95.3968 47.6681 96.2408 48.2254 96.9716 48.8854C97.6985 49.5222 98.123 50.1987 98.2452 50.9149C98.3398 51.4694 98.349 51.872 98.2729 52.1228C97.128 50.9867 95.8104 50.0227 94.3201 49.2308C92.544 48.2974 90.9629 47.949 89.5767 48.1855C88.2598 48.4102 87.5415 49.4957 87.4218 51.442C87.3795 52.4478 87.51 53.8402 87.8136 55.6192C88.1131 57.3751 88.4353 58.9845 88.78 60.4473C89.1207 61.8871 89.6483 63.3069 90.3626 64.7067C91.7951 64.4623 93.3791 64.0612 95.1147 63.5036C98.4434 62.3888 100.857 61.6917 102.354 61.4124Z" fill="#3B65EF" />
        <path d="M120.365 57.2339C119.277 56.7063 117.97 56.5726 116.445 56.8328C115.013 57.0772 113.729 57.5695 112.596 58.3098C110.451 59.6744 109.081 61.192 108.486 62.8627C108.235 63.6189 108.176 64.3897 108.31 65.1752C108.334 65.3139 108.401 65.7066 108.511 66.3535C108.618 66.9773 108.879 67.8124 109.296 68.8589C109.708 69.8822 110.009 70.9484 110.198 72.0574C109.717 72.1633 109.334 72.288 109.049 72.4317C108.765 72.5753 108.518 72.6649 108.31 72.7004C108.102 72.7359 107.902 72.675 107.709 72.5178C107.538 72.3566 107.316 72.0261 107.04 71.5262C106.993 71.3915 106.88 71.0067 106.7 70.3716C106.521 69.7365 106.294 68.9667 106.021 68.0623C105.771 67.1539 105.484 66.1686 105.16 65.1064C104.855 64.0172 104.555 62.951 104.258 61.9079C103.61 59.6409 103.123 57.9763 102.8 56.9141C102.705 56.3596 103.062 56.0134 103.871 55.8754C104.356 55.7926 104.772 55.9355 105.121 56.3041C105.465 56.6496 105.763 57.0742 106.016 57.578C106.264 58.0587 106.455 58.5492 106.588 59.0496C106.744 59.546 106.863 59.8943 106.944 60.0943C109.529 56.8478 113.085 54.8382 117.613 54.0656C118.122 53.9789 118.655 53.8998 119.214 53.8283C119.348 53.7815 119.52 53.7404 119.727 53.7049C119.935 53.6694 120.167 53.7726 120.422 54.0143C120.732 54.2944 120.926 54.6655 121.004 55.1276C121.202 56.2828 120.988 56.9849 120.365 57.2339Z" fill="#3B65EF" />
        <path d="M131.744 65.8134C133.777 65.4666 136.68 64.0201 140.454 61.4741C140.709 61.4307 140.932 61.4877 141.125 61.6449C141.315 61.779 141.429 61.9616 141.469 62.1927C140.813 63.9214 139.254 65.4474 136.793 66.7708C134.332 68.0942 131.92 68.6602 129.558 68.4689C126.879 68.2365 125.073 67.0586 124.14 64.9352C124.019 64.5042 123.895 63.919 123.769 63.1796C123.643 62.4403 123.662 61.4384 123.827 60.174C124.011 58.8825 124.472 57.6149 125.211 56.3714C125.95 55.1279 126.89 54.0878 128.031 53.2511C129.172 52.4144 130.448 51.8759 131.857 51.6354C133.197 51.4068 134.777 51.6126 136.599 52.253C137.459 52.5579 138.168 52.9482 138.724 53.4239C139.281 53.8995 139.609 54.4261 139.707 55.0037C139.735 55.1654 139.751 55.3291 139.755 55.4948C139.949 56.912 139.42 57.9178 138.166 58.5121C137.616 58.7724 136.96 58.9676 136.197 59.0977L131.171 59.7412C129.508 60.025 128.298 60.5999 127.543 61.4659C127.101 61.9454 126.844 62.5956 126.77 63.4166C127.706 65.4205 129.364 66.2195 131.744 65.8134ZM136.085 54.0524C135.514 53.7695 134.937 53.5945 134.355 53.5273C133.773 53.4602 133.297 53.4582 132.928 53.5213C132.558 53.5843 131.979 53.8139 131.191 54.2099C130.398 54.5829 129.704 54.9747 129.109 55.3854C127.641 56.3967 126.976 57.3067 127.114 58.1153C128.989 57.9617 130.389 57.8061 131.314 57.6484C133.878 57.2108 135.573 56.6957 136.4 56.103C136.915 55.7774 137.143 55.4413 137.083 55.0947C137.012 54.6788 136.68 54.3314 136.085 54.0524Z" fill="#3B65EF" />
        <path d="M150.943 62.5377C152.976 62.1908 155.88 60.7444 159.654 58.1984C159.908 58.155 160.131 58.212 160.325 58.3692C160.514 58.5033 160.628 58.6859 160.668 58.917C160.012 60.6457 158.453 62.1717 155.992 63.4951C153.531 64.8185 151.119 65.3845 148.757 65.1932C146.078 64.9608 144.272 63.7829 143.339 61.6595C143.218 61.2284 143.094 60.6432 142.968 59.9039C142.842 59.1646 142.861 58.1627 143.026 56.8983C143.21 55.6068 143.671 54.3392 144.41 53.0957C145.149 51.8521 146.089 50.812 147.23 49.9754C148.371 49.1387 149.647 48.6002 151.056 48.3597C152.396 48.1311 153.977 48.3369 155.798 48.9773C156.658 49.2822 157.367 49.6725 157.923 50.1481C158.48 50.6238 158.808 51.1504 158.906 51.728C158.934 51.8897 158.95 52.0534 158.954 52.2191C159.149 53.6363 158.619 54.6421 157.365 55.2364C156.815 55.4967 156.159 55.6919 155.396 55.822L150.37 56.4655C148.707 56.7493 147.497 57.3242 146.742 58.1902C146.3 58.6697 146.043 59.3199 145.969 60.1409C146.905 62.1448 148.563 62.9437 150.943 62.5377ZM155.284 50.7767C154.713 50.4938 154.136 50.3187 153.554 50.2516C152.972 50.1845 152.496 50.1825 152.127 50.2456C151.757 50.3086 151.178 50.5382 150.39 50.9342C149.597 51.3072 148.903 51.699 148.308 52.1097C146.84 53.121 146.175 54.031 146.313 54.8396C148.189 54.686 149.589 54.5304 150.513 54.3727C153.077 53.9351 154.773 53.42 155.599 52.8273C156.114 52.5017 156.342 52.1656 156.283 51.819C156.212 51.4031 155.879 51.0557 155.284 50.7767Z" fill="#3B65EF" />
        <path fillRule="evenodd" clipRule="evenodd" d="M0.360744 85.8592C0.042731 86.4044 0.100002 87.0903 0.504021 87.5752L5.50329 93.5752C6.0336 94.2116 6.97944 94.2977 7.61589 93.7674C8.25234 93.2371 8.33839 92.2912 7.80809 91.6548L4.96026 88.2369C34.2401 88.9563 53.3904 80.0132 59.7177 62.223C59.9953 61.4425 59.5876 60.5847 58.8071 60.3071C58.0265 60.0295 57.1687 60.4372 56.8911 61.2177C51.1792 77.2778 33.5375 86.1203 4.20947 85.2153L6.45209 81.3707C6.8695 80.6551 6.62779 79.7366 5.91221 79.3192C5.19662 78.9018 4.27815 79.1435 3.86074 79.8591L0.360744 85.8592Z" fill="#3B65EF" />
    </svg>;


const TitleLayout = () =>
    <Flexer flex="column" center margin="3 0 5">
        <PromoTitle>
            Transform Your Teamwork
        </PromoTitle>
        <PromoSubTitle>
            With Our Flexible Plans
        </PromoSubTitle>
    </Flexer>;

const UpgradeLayout = () => {

    const { trial, plan } = useResolve(AccountModel);
    const intervalOpts = useMemo(() => [LabelValue('Monthly', 0), LabelValue('Annual', 1)], []);
    const [interval, setInterval] = useState(0);

    return <Flexer flex="column" align="center">
        <Flexer relative flex center margin="0 0 2">
            <ButtonToggle options={intervalOpts} selected={interval} onChange={setInterval} />
            <Flexer absolute transform="translate(10rem,-1.2rem) scale(0.7) rotate(5deg)">
                <UpgradeAnnualGraphic />
            </Flexer>
        </Flexer>
        <Flexer flex="row" width="70" justify="space-between" padding="0">
            {plans.map(x =>
                <UpgradeOption key={x.title} color={x.color}>
                    <Flexer flex="column" center gap=".25">
                        <Title color={x.color} flush size="md">
                            <span>{x.title}</span>
                        </Title>
                        <UpgradeCost>
                            {x.pricing[interval]}
                        </UpgradeCost>
                        {x.pricingNote &&
                            <UpgradeCostNote>
                                {x.pricingNote[interval]}
                            </UpgradeCostNote>
                        }
                    </Flexer>
                    <UpgradeDesc>
                        {x.desc}
                    </UpgradeDesc>
                    <Flexer>
                        {x.features.map((fc, i) =>
                            <Flexer key={i.toString()}>
                                {fc.map(f => <UpgradeFeature key={f}>{f}</UpgradeFeature>)}
                            </Flexer>
                        )}
                    </Flexer>
                    <Flexer flex="column" align="center" padding="1 0 0 0" style={{ marginTop: 'auto' }}>
                        {!!trial && plan.name.includes(x.name) && <Flexer padding="0 1.5 1.5">
                            {trial.state === 'active' && <UpgradeStatus>
                                You have {differenceInDays(trial.expiresAt, new Date())} days remaining on your {plan.label} after which your space will revert to the Free Plan.
                            </UpgradeStatus>}
                            {trial.state === 'expired' && <UpgradeStatus>
                                Your free trial has now expired.  Feel free to upgrade at any time!
                            </UpgradeStatus>}
                        </Flexer>}
                        {x.action === 'buy' && <>
                            <Flexer flex="row" center gap=".5">
                                <LinkButton variant={x.color === Colors.positive ? 'positive' : 'mystery'} size="xl" target="_blank" to={`${Host.baseUrl}/checkout/select?type=${x.name}&interval=${interval}`}>
                                    Buy Now
                                </LinkButton>
                                {!trial && <>
                                    <LinkButton target="_blank" to={`${Host.baseUrl}/checkout/trial?type=${x.name}`} rel="noreferrer" size="xl" variant="common">
                                        Start Trial
                                    </LinkButton>
                                </>}
                            </Flexer>
                        </>}
                        {x.action === 'contact' && <>
                            <Flexer flex="row" center gap=".5">
                                <LinkButton variant={x.color === Colors.positive ? 'positive' : 'mystery'} size="xl" target="_blank" to="https://calendly.com/jamie-metroretro/">
                                    Book a call
                                </LinkButton>
                            </Flexer>
                        </>}
                        {trial?.state !== 'active' && plan.name.includes(x.name) && <Flexer flex="row" center>
                            <Flexer width="1.5" margin="0 .5 0 0">
                                <Check.On color={shade(.1, x.color)} />
                            </Flexer>
                            <Title color={x.color} flush size="xl">Current Plan</Title>
                        </Flexer>}
                    </Flexer>
                </UpgradeOption>
            )}
        </Flexer>
    </Flexer>;
};

const TestimonalLayout = () => {

    useEffect(() => {
        setTimeout(() => iframeResize({ log: false, checkOrigin: false }, '#testimonialto-metro-retro-tag-all-light'), 1000);
    }, []);

    return <>
        <Flexer flex="column" center margin="5 0 4" opacity=".75">
            <Title size="2xl" color={Colors.text} weight="500">
                Still not convinced?
            </Title>
            <Title size="xl" color={Colors.text}>
                Look what others are saying...
            </Title>
        </Flexer>
        <Flexer>
            <iframe id="testimonialto-metro-retro-tag-all-light" src="https://embed-v2.testimonial.to/w/metro-retro?theme=light&card=base&loadMore=on&initialCount=10&tag=all" frameBorder="0" scrolling="no" width="100%"></iframe>
        </Flexer>
    </>;
};

const Branding = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
    font-size: 1.6rem;
    color: ${Colors.primary};
    font-weight: 450;

    img {
        width: 3rem;
        height: 3rem;
    }

    cursor: pointer;
`;

const PromoTitle = styled.div`
    font-weight: 500;
    font-size: 3rem;
    line-height: 1.25em;
    color: ${Colors.text};
    text-align: center;
`;

const PromoSubTitle = styled.div`
    font-weight: 500;
    font-size: 1.5rem;
    line-height: 1.25em;
    color: ${Colors.text};
    text-align: center;
`;

export const upgradePopup = () => ({ ui: UpgradePopup, fullscreen: true });

export const UpgradePopup: LFC<PopupProps> = ({ action, dismiss, args }) => {
    const user = useResolve(ProfileModel);
    const { id } = useResolve(AccountModel);

    useEffect(() => {
        telemetry.emit('popup.pricing', { userId: user.id, accountId: id });
    }, []);

    return <Prompt ignoreEnter onAction={dismiss}>
        <Popup p="0" background="#F3F7FB">
            <XScrollbars className="scrollbars">
                <Flexer padding="2">
                    <Popup.Content>
                        <TitleLayout />
                        <UpgradeLayout />
                        <TestimonalLayout />
                    </Popup.Content>
                    <Position at="topLeft" inset="1">
                        <Branding onClick={dismiss}>
                            <img src={Images.LogoBlue} />
                            Metro Retro
                        </Branding>
                    </Position>
                </Flexer>
            </XScrollbars>
            <Position at="topRight" inset="2">
                <DropButton rounding={1000} onClick={dismiss}>
                    <CloseButton />
                </DropButton>
            </Position>
        </Popup>
    </Prompt>;
};

export const InvokeUpgradePopupIfApplicable = () => {
    const popups = useResolve(PopupManager);
    useEffectOnce(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('action') !== 'setup') return;
        window.history.replaceState({}, document.title, window.location.pathname);
        waitFor(200).then(async () => {
            await popups.invoke(upgradePopup());
        });
    });
    return null;
};
